// Admin Dashboard — the index of every admin surface.
//
// WHAT CHANGED, AND WHY
//
// It was thirteen equal-weight cards in a flat two-column grid, each with a 2px
// border in its own color: blue, green, amber, purple, rose, orange, cyan,
// yellow, teal and primary. Ten accents encoding nothing — Serial Number
// Generator was green and API Keys was rose for no reason either could tell you.
// Color that carries no meaning is noise, and at thirteen cards it is a lot of
// noise on the page somebody lands on when they do not already know where they
// are going.
//
// Two changes, both structural rather than decorative:
//
// 1. GROUPED. Thirteen undifferentiated things is a wall; five groups of two or
//    three is a menu. The groups answer "what am I here to do" — manage people,
//    define the shapes data comes in, run the shop floor, control money, wire up
//    integrations.
//
// 2. ONE ACCENT. Primary, which is instance-aware (gold on Firefleet, blue on
//    SwarmSystems) and already carries meaning everywhere else in the app.
//    Structure comes from the group headings now, which is what structure is for.
//
// And one correctness fix that is not cosmetic: TILES ARE GATED. Every route
// below has its own capability check in App.js, and several are stricter than
// this page's own `isadmin` gate — /admin/cards additionally accepts
// can_financial, /admin/locations and friends accept can_admin. A tile that
// bounces you back to / the moment you click it is a dead end, and the house
// rule is that every state has an exit. Now you only see what you can open.
//
// That fix covered CAPABILITY and missed INSTANCE, which is the other half of
// the same sentence: Manufacturing Report and AI Controls are Firefleet-only
// and still rendered on SwarmSystems, dead-ending exactly as described above.
// Both filters now run, off the route registry. See the note in the component.

import { Link } from 'react-router-dom';
import useUserStore from '../stores/useUserStore';
import { routeAllowedOnInstance } from '../navigation/routeInstances';
import PageLayout from '../components/ui/PageLayout';

/** The capability flags a tile's `can` reads. */
interface AdminUser {
    isadmin?: boolean;
    can_admin?: boolean;
    can_financial?: boolean;
}

interface AdminTile {
    title: string;
    description: string;
    link: string;
    can: (u: AdminUser) => boolean;
}

interface AdminGroup {
    heading: string;
    items: AdminTile[];
}

// `can` mirrors the route's own guard in App.js. Keep them in step: this
// function decides whether the tile RENDERS, App.js decides whether the page
// does, and only the second one is security.
const GROUPS: AdminGroup[] = [
    {
        heading: 'People and access',
        items: [
            {
                title: 'User Management',
                description: 'Add people, edit their details and capabilities, deactivate accounts',
                link: '/admin/users',
                can: (u) => u.isadmin,
            },
            {
                title: 'API Keys',
                description: 'Create and revoke keys for external integrations',
                link: '/admin/apikeys',
                can: (u) => u.isadmin,
            },
            {
                title: 'Scanners',
                description: 'Enroll shop-floor handhelds with a code, and revoke lost ones',
                link: '/admin/scanners',
                can: (u) => u.isadmin,
            },
        ],
    },
    {
        heading: 'Reference data',
        items: [
            {
                title: 'Locations',
                description:
                    'Where stock can be. A new deployment starts with none and cannot receive anything until a warehouse exists here',
                link: '/admin/locations',
                can: (u) => u.isadmin || u.can_admin,
            },
            {
                title: 'Part Families',
                description: 'Rollup buckets for the catalog, and their stale-box alert thresholds',
                link: '/admin/part-families',
                can: (u) => u.isadmin || u.can_admin,
            },
            {
                title: 'Asset Types',
                description: 'Non-drone gear types (chargers, tarps, cables) and their barcode prefixes',
                link: '/admin/asset-types',
                can: (u) => u.isadmin || u.can_admin,
            },
            {
                title: 'RMA Catalog',
                description: 'Items and box sizes offered on RMA submissions',
                link: '/rma/catalog',
                can: (u) => u.isadmin,
            },
        ],
    },
    {
        heading: 'Shop floor',
        items: [
            {
                title: 'Serial Number Generator',
                description: 'Mint serial numbers for new drones',
                link: '/serialgen',
                can: (u) => u.isadmin,
            },
            {
                title: 'Flash Operations',
                description: 'Flash station statistics, the operations log, and the board registry',
                link: '/admin/flash-operations',
                can: (u) => u.isadmin,
            },
            {
                title: 'Ghost Flags Detector',
                description: 'Find and clear flags still active on drones marked ready to fly',
                link: '/admin/ghost-flags',
                can: (u) => u.isadmin,
            },
            {
                title: 'Records to reconcile',
                description: 'Drones a shipment scan has confirmed, and every shipment line recorded over a warning',
                link: '/admin/reconcile',
                can: (u) => u.isadmin || u.can_admin,
            },
            {
                title: 'Manufacturing Report',
                description: 'Branded PDF of drones built, by month and ISO week',
                link: '/admin/manufacturing-report',
                can: (u) => u.isadmin,
            },
        ],
    },
    {
        heading: 'Money',
        items: [
            {
                title: 'Approval Settings',
                description: 'Who approves purchase orders, and the dollar threshold that requires it',
                link: '/admin/approval-settings',
                can: (u) => u.isadmin || u.can_admin,
            },
            {
                title: 'Company Cards',
                description: 'Assign cards to people so each sees only their own in the receipt dropdown',
                link: '/admin/cards',
                can: (u) => u.isadmin || u.can_admin || u.can_financial,
            },
        ],
    },
    {
        heading: 'Integrations',
        items: [
            {
                title: 'AI Controls',
                description: 'GitBook spaces, usage statistics, and Flerpie configuration',
                link: '/admin/ai',
                can: (u) => u.isadmin,
            },
            {
                title: 'Asana import',
                description: 'Bring the Studio\'s Asana board into Spark, one project at a time',
                link: '/admin/asana-import',
                can: (u) => u.isadmin || u.can_admin,
            },
        ],
    },
];

const AdminPage = () => {
    const user: AdminUser = useUserStore((s: any) => s.user);

    // TWO independent filters, and both have to pass. Capability answers "may
    // this person?", instance answers "does this deployment have it at all?" —
    // orthogonal questions, neither folding into the other.
    //
    // The instance half was missing, so Manufacturing Report and AI Controls
    // rendered on SwarmSystems and bounced straight back to / when clicked.
    // Neither carries `instances` in navTree, which under the allowlist rule
    // means Firefleet-only, and InstanceGate enforces exactly that.
    //
    // Derived from the registry rather than listed here on purpose. A hardcoded
    // pair would be right today and wrong the next time a route moves between
    // instances, and this page is the last place anybody would think to look.
    const groups = GROUPS
        .map((g) => ({
            ...g,
            items: g.items.filter((i) => i.can(user) && routeAllowedOnInstance(i.link)),
        }))
        .filter((g) => g.items.length > 0);

    return (
        <PageLayout kind="workspace" className="text-on-surface pt-6">
            <div>
                <header className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight">Admin</h1>
                    <p className="text-sm text-on-surface-variant mt-1">
                        Settings and reference data. Everything here shapes how the rest of the app
                        behaves, so changes are visible to everyone.
                    </p>
                </header>

                <div className="space-y-8">
                    {groups.map((group) => (
                        <section key={group.heading}>
                            <div className="flex items-center gap-3 mb-3">
                                <h2 className="text-[11px] uppercase tracking-wider text-on-surface-variant font-semibold">
                                    {group.heading}
                                </h2>
                                <div className="flex-1 h-px bg-gray-700/50" />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                                {group.items.map((item) => (
                                    <Link
                                        key={item.link}
                                        to={item.link}
                                        className="group bg-surface-container-low border border-transparent rounded-md p-4
                                                   transition-colors hover:border-primary/50 hover:bg-surface-container
                                                   focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                                    >
                                        <h3 className="text-sm font-semibold text-on-surface group-hover:text-primary transition-colors">
                                            {item.title}
                                        </h3>
                                        <p className="text-xs text-on-surface-variant mt-1 leading-relaxed">
                                            {item.description}
                                        </p>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </PageLayout>
    );
};

export default AdminPage;
